if (!customElements.get("ifa-community-stories")) {
  class IfaCommunityStories extends HTMLElement {
    connectedCallback() {
      if (this.abortController) return;

      this.abortController = new AbortController();
      this.viewport = this.querySelector("[data-story-viewport]");
      this.track = this.querySelector("[data-story-track]");
      this.banner = this.querySelector("[data-story-banner]");
      this.cards = Array.from(this.querySelectorAll("[data-story-card]"));
      this.storySlides = this.cards.map((card) => card.closest(".shopify-block") || card.closest(".ifa-community-stories__item") || card);
      this.templates = Array.from(this.querySelectorAll("[data-story-template]"));
      this.previousButton = this.querySelector("[data-story-previous]");
      this.nextButton = this.querySelector("[data-story-next]");
      this.dialog = this.querySelector("[data-story-dialog]");
      this.dialogBody = this.querySelector("[data-story-dialog-body]");
      this.closeButton = this.querySelector("[data-story-close]");
      this.currentIndex = 0;
      this.resizeFrame = 0;
      this.scrollFrame = 0;
      this.scrollAnimFrame = 0;
      this.isAnimating = false;
      this.hasLeftViewport = false;
      this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      this.scrollDuration = 560;

      const signal = this.abortController.signal;

      this.previousButton?.addEventListener("click", () => this.move(-1), { signal });
      this.nextButton?.addEventListener("click", () => this.move(1), { signal });
      this.closeButton?.addEventListener("click", () => this.closeDialog(), { signal });
      this.viewport?.addEventListener("scroll", () => this.handleScroll(), { signal, passive: true });
      this.track?.addEventListener("scroll", () => this.handleScroll(), { signal, passive: true });
      window.addEventListener("resize", () => this.handleResize(), { signal, passive: true });

      this.visibilityObserver = new IntersectionObserver((entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (!entry.isIntersecting) {
          this.hasLeftViewport = true;
          return;
        }

        if (this.hasLeftViewport) this.resetProgress();
      }, { threshold: 0.2 });
      this.visibilityObserver.observe(this);

      this.cards.forEach((card) => {
        card.addEventListener("click", () => this.openDialog(card.dataset.storyCard), { signal });
      });

      this.dialog?.addEventListener("click", (event) => {
        if (event.target === this.dialog) this.closeDialog();
      }, { signal });
      this.dialog?.addEventListener("close", () => this.clearDialog(), { signal });

      this.updateControls();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.abortController = null;
      this.visibilityObserver?.disconnect();
      this.visibilityObserver = null;
      cancelAnimationFrame(this.resizeFrame);
      cancelAnimationFrame(this.scrollFrame);
      cancelAnimationFrame(this.scrollAnimFrame);
      this.endProgrammaticScroll();
      this.clearDialog();
    }

    visibleCount() {
      if (window.matchMedia("(min-width: 1280px)").matches) return 3;
      if (window.matchMedia("(min-width: 768px)").matches) return 2;
      return 1;
    }

    slides() {
      if (window.matchMedia("(min-width: 768px)").matches && this.banner) {
        return [this.banner, ...this.storySlides];
      }

      return this.storySlides;
    }

    scroller() {
      if (window.matchMedia("(min-width: 768px)").matches) return this.viewport;
      return this.track;
    }

    isMobile() {
      return !window.matchMedia("(min-width: 768px)").matches;
    }

    maxIndex() {
      const slides = this.slides();
      const visibleCount = this.visibleCount();
      if (!slides.length) return 0;
      return Math.max(0, slides.length - visibleCount);
    }

    move(direction) {
      if (this.isAnimating) return;

      const step = this.visibleCount();
      const maxIndex = this.maxIndex();
      const nextIndex = Math.max(0, Math.min(maxIndex, this.currentIndex + (direction * step)));
      if (nextIndex === this.currentIndex) return;

      this.scrollToIndex(nextIndex);
    }

    scrollToIndex(index, behavior = "smooth") {
      const slides = this.slides();
      const scroller = this.scroller();
      const slide = slides[index];
      if (!slide || !scroller) return;

      this.currentIndex = index;
      this.updateControls();

      const isFinalPosition = index >= this.maxIndex();
      const maxScroll = scroller.scrollWidth - scroller.clientWidth;
      const targetLeft = this.isMobile()
        ? index === 0
          ? 0
          : isFinalPosition
            ? maxScroll
            : this.slideOffset(slide, scroller)
        : isFinalPosition
          ? maxScroll
          : this.slideOffset(slide, scroller);
      const distance = Math.abs(targetLeft - scroller.scrollLeft);
      // Longer travel for grouped jumps needs a bit more time to stay soft.
      const duration = Math.min(720, Math.max(this.scrollDuration, distance * 0.55));

      if (behavior === "auto" || this.reducedMotion) {
        this.cancelProgrammaticScroll();
        scroller.scrollLeft = targetLeft;
        return;
      }

      this.animateScrollTo(scroller, targetLeft, duration);
    }

    animateScrollTo(scroller, targetLeft, duration = this.scrollDuration) {
      this.cancelProgrammaticScroll();

      const startLeft = scroller.scrollLeft;
      const delta = targetLeft - startLeft;

      if (Math.abs(delta) < 1) {
        scroller.scrollLeft = targetLeft;
        return;
      }

      this.isAnimating = true;
      scroller.classList.add("is-scrolling");

      const startTime = performance.now();

      const tick = (now) => {
        const progress = Math.min(1, (now - startTime) / duration);
        // Softer ease-in-out for grouped page turns.
        const eased = progress < 0.5
          ? 4 * progress * progress * progress
          : 1 - ((-2 * progress + 2) ** 3) / 2;

        scroller.scrollLeft = startLeft + (delta * eased);

        if (progress < 1) {
          this.scrollAnimFrame = requestAnimationFrame(tick);
          return;
        }

        scroller.scrollLeft = targetLeft;
        this.endProgrammaticScroll();
      };

      this.scrollAnimFrame = requestAnimationFrame(tick);
    }

    cancelProgrammaticScroll() {
      cancelAnimationFrame(this.scrollAnimFrame);
      this.scrollAnimFrame = 0;
      this.endProgrammaticScroll();
    }

    endProgrammaticScroll() {
      this.isAnimating = false;
      this.viewport?.classList.remove("is-scrolling");
      this.track?.classList.remove("is-scrolling");
    }

    slideOffset(slide, scroller) {
      const scrollerRect = scroller.getBoundingClientRect();
      const slideRect = slide.getBoundingClientRect();

      if (this.isMobile()) {
        const targetLeft = scroller.scrollLeft
          + slideRect.left
          - scrollerRect.left
          - ((scroller.clientWidth - slideRect.width) / 2);
        const maxScroll = Math.max(0, scroller.scrollWidth - scroller.clientWidth);

        return Math.min(maxScroll, Math.max(0, targetLeft));
      }

      const scrollPaddingStart = Number.parseFloat(
        window.getComputedStyle(scroller).scrollPaddingInlineStart
      ) || 0;

      return Math.max(
        0,
        scroller.scrollLeft + slideRect.left - scrollerRect.left - scrollPaddingStart
      );
    }

    handleScroll() {
      if (this.isAnimating) return;

      cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = requestAnimationFrame(() => {
        const slides = this.slides();
        const scroller = this.scroller();
        if (!scroller || !slides.length) return;

        const left = scroller.scrollLeft;
        let closestIndex = 0;
        let closestDistance = Infinity;
        slides.forEach((slide, index) => {
          const distance = Math.abs(this.slideOffset(slide, scroller) - left);
          if (distance < closestDistance) {
            closestDistance = distance;
            closestIndex = index;
          }
        });
        this.currentIndex = Math.min(closestIndex, this.maxIndex());
        this.updateControls();
      });
    }

    handleResize() {
      cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame = requestAnimationFrame(() => {
        const maxIndex = this.maxIndex();
        this.scrollToIndex(Math.min(this.currentIndex, maxIndex), "auto");
      });
    }

    resetProgress() {
      const scroller = this.scroller();
      if (!scroller) return;

      this.cancelProgrammaticScroll();
      this.currentIndex = 0;
      scroller.scrollLeft = 0;
      this.updateControls();
    }

    updateControls() {
      const maxIndex = this.maxIndex();
      this.previousButton?.toggleAttribute("disabled", this.currentIndex <= 0);
      this.nextButton?.toggleAttribute("disabled", this.currentIndex >= maxIndex);
    }

    openDialog(index) {
      if (!this.dialog || !this.dialogBody) return;

      const template = this.templates.find((item) => item.dataset.storyTemplate === index);
      if (!template) return;

      this.clearDialog();
      this.dialogBody.appendChild(template.content.cloneNode(true));
      this.dialog.showModal();
      this.closeButton?.focus({ preventScroll: true });
    }

    closeDialog() {
      if (this.dialog?.open) this.dialog.close();
    }

    clearDialog() {
      this.dialogBody?.replaceChildren();
    }
  }

  customElements.define("ifa-community-stories", IfaCommunityStories);
}
